import { flexStyle, textStyle } from '@themes/mixins';
import styled, { DefaultTheme } from 'styled-components';

import { ValidationOutcome, ValidationStatus } from '../../types';

const outcomeColor = (theme: DefaultTheme, outcome: ValidationOutcome) => {
  if (outcome === 'match') return theme.colors.success;
  if (outcome === 'mismatch') return theme.colors.danger;
  return theme.colors.warning;
};

const statusColor = (theme: DefaultTheme, status: ValidationStatus) => {
  if (status === 'match') return theme.colors.success;
  if (status === 'mismatch') return theme.colors.danger;
  if (status === 'not_verified') return theme.colors.warning;
  return theme.colors.textSecondary;
};

export const Root = styled.div`
  ${flexStyle({ direction: 'column' })}
  gap: 16px;
`;

export const Banner = styled.div<{ $outcome: ValidationOutcome }>`
  ${flexStyle({ direction: 'column' })}
  gap: 4px;
  padding: 12px 14px;
  border-radius: 6px;
  border-left: 4px solid
    ${({ theme, $outcome }) => outcomeColor(theme, $outcome)};
  background: ${({ theme }) => theme.colors.secondaryBackground};
`;

export const BannerTitle = styled.div<{ $outcome: ValidationOutcome }>`
  ${textStyle({ weight: 600, size: '15px' })}
  color: ${({ theme, $outcome }) => outcomeColor(theme, $outcome)};
`;

export const BannerDetail = styled.div`
  ${textStyle({ weight: 400, size: '12px', color: 'textSecondary' })}
`;

export const Section = styled.div`
  ${flexStyle({ direction: 'column' })}
  gap: 2px;
`;

export const SectionTitle = styled.div`
  ${textStyle({ weight: 600, size: '13px', color: 'textPrimary' })}
  padding-bottom: 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

export const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 1.4fr) 1fr 1fr auto;
  align-items: baseline;
  gap: 10px;
  padding: 5px 0;
`;

export const RowHead = styled(Row)`
  ${textStyle({ weight: 600, size: '11px', color: 'textSecondary' })}
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

export const Label = styled.div`
  ${textStyle({ weight: 400, size: '13px', color: 'textPrimary' })}
`;

export const Value = styled.div<{ $muted?: boolean }>`
  ${textStyle({ weight: 400, size: '13px' })}
  color: ${({ theme, $muted }) =>
    $muted ? theme.colors.textSecondary : theme.colors.textPrimary};
  word-break: break-word;
`;

export const StatusChip = styled.span<{ $status: ValidationStatus }>`
  ${textStyle({ weight: 600, size: '11px' })}
  justify-self: end;
  white-space: nowrap;
  padding: 2px 8px;
  border-radius: 10px;
  color: ${({ theme, $status }) => statusColor(theme, $status)};
  border: 1px solid ${({ theme, $status }) => statusColor(theme, $status)};
`;

export const InferredMark = styled.span`
  ${textStyle({ weight: 400, size: '11px', color: 'textSecondary' })}
  margin-left: 6px;
  font-style: italic;
`;

export const Note = styled.div`
  ${textStyle({ weight: 400, size: '11px', color: 'textSecondary' })}
  padding: 0 0 6px 2px;
`;

export const ToggleButton = styled.button`
  ${textStyle({ weight: 500, size: '12px', color: 'linkBlue' })}
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 0;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const DocumentLine = styled.div`
  ${textStyle({ weight: 400, size: '12px', color: 'textSecondary' })}
`;

export const StructuralFinding = styled.div`
  ${textStyle({ weight: 400, size: '13px', color: 'textPrimary' })}
`;
